import clsx from 'clsx';
import {Fragment, ReactNode} from 'react';

export interface StaffAgreementData {
  id: number;
  version: number;
  title_en: string;
  title_ar: string;
  body_en: string;
  body_ar: string;
  published_at: string | null;
}

export type AgreementLang = 'en' | 'ar';

/**
 * The agreement body is plain text with three conventions, so it can be
 * edited in a textarea: "## " starts a heading, "1. " a numbered item,
 * "- " a bullet. A blank line separates paragraphs.
 */
export function AgreementText({
  agreement,
  lang,
  className,
}: {
  agreement: StaffAgreementData;
  lang: AgreementLang;
  className?: string;
}) {
  const body = lang === 'en' ? agreement.body_en : agreement.body_ar;
  return (
    <div
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      lang={lang}
      className={clsx('text-sm leading-relaxed', className)}
    >
      {renderAgreementBody(body)}
    </div>
  );
}

export function agreementTitle(a: StaffAgreementData, lang: AgreementLang) {
  return lang === 'en' ? a.title_en : a.title_ar;
}

export function renderAgreementBody(body: string): ReactNode {
  const blocks: ReactNode[] = [];
  let list: {ordered: boolean; items: string[]} | null = null;
  const flush = () => {
    if (!list) return;
    const Tag = list.ordered ? 'ol' : 'ul';
    blocks.push(
      <Tag
        key={blocks.length}
        className={clsx(
          'mb-12 space-y-4 ps-20',
          list.ordered ? 'list-decimal' : 'list-disc',
        )}
      >
        {list.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </Tag>,
    );
    list = null;
  };

  body.split(/\r?\n/).forEach(raw => {
    const line = raw.trim();
    if (!line) {
      flush();
      return;
    }
    const heading = line.match(/^##\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (heading) {
      flush();
      blocks.push(
        <h3 key={blocks.length} className="mb-8 mt-20 text-base font-bold">
          {heading[1]}
        </h3>,
      );
    } else if (numbered || bullet) {
      const ordered = !!numbered;
      if (list && list.ordered !== ordered) flush();
      if (!list) list = {ordered, items: []};
      list.items.push((numbered ?? bullet)![1]);
    } else {
      flush();
      blocks.push(
        <p key={blocks.length} className="mb-12">
          {line}
        </p>,
      );
    }
  });
  flush();
  return <Fragment>{blocks}</Fragment>;
}
